import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
export type CartItem = {
  key: string;
  slug: string;
  name: string;
  option?: string;
  price: number;
  qty: number;
  image?: string;
};
type CartContextValue = {
  items: CartItem[];
  count: number;
  subtotal: number;
  addItem: (item: Omit<CartItem, 'key'>) => void;
  updateQty: (key: string, qty: number) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const addItem = useCallback((item: Omit<CartItem, 'key'>) => {
    const key = `${item.slug}::${item.option ?? ''}`;
    setItems(prev => {
      const existing = prev.find(i => i.key === key);
      if (existing) {
        return prev.map(i => i.key === key ? {
          ...i,
          qty: i.qty + item.qty
        } : i);
      }
      return [...prev, {
        ...item,
        key
      }];
    });
  }, []);
  const updateQty = useCallback((key: string, qty: number) => {
    setItems(prev => qty <= 0 ? prev.filter(i => i.key !== key) : prev.map(i => i.key === key ? {
      ...i,
      qty
    } : i));
  }, []);
  const removeItem = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);
  const clear = useCallback(() => setItems([]), []);
  const value = useMemo<CartContextValue>(() => ({
    items,
    count: items.reduce((n, i) => n + i.qty, 0),
    subtotal: items.reduce((n, i) => n + i.qty * i.price, 0),
    addItem,
    updateQty,
    removeItem,
    clear
  }), [items, addItem, updateQty, removeItem, clear]);
  return <CartContext.Provider value={value} data-id="element-294" data-mp="gsvxpymbpnxs">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}